import { DUST_LIMIT } from '../../constants';
import type { FeePriority, UTXO } from '../../WaasBitcoinWalletProvider.types';
import { estimateTransactionFee } from '../estimateTransactionFee';

type CalculateFeeAndChangeOptions = {
  amountInSatoshis: bigint;
  feePriority: FeePriority;
  selectedTotalValue: number;
  selectedUTXOs: UTXO[];
};

type CalculateFeeAndChangeResult = {
  changeAmountNumber: number;
  feeEstimate: number;
  hasChangeOutput: boolean;
};

/**
 * Calculates fee estimate and change amount, handling dust limit
 *
 * @param options.amountInSatoshis - Amount to send in satoshis
 * @param options.feePriority - Priority level for fee estimation ('low', 'medium', or 'high')
 * @param options.selectedTotalValue - Total value of selected UTXOs in satoshis
 * @param options.selectedUTXOs - Array of selected UTXOs for the transaction
 * @returns Object with feeEstimate, changeAmountNumber, and hasChangeOutput
 */
export const calculateFeeAndChange = async ({
  amountInSatoshis,
  feePriority,
  selectedTotalValue,
  selectedUTXOs,
}: CalculateFeeAndChangeOptions): Promise<CalculateFeeAndChangeResult> => {
  let feeEstimate = await estimateTransactionFee({
    feePriority,
    numInputs: selectedUTXOs.length,
    numOutputs: 1,
  });

  let maxToSpend = selectedTotalValue - feeEstimate;
  let changeAmount = BigInt(maxToSpend) - amountInSatoshis;

  const changeAmountNumber = Number(changeAmount);

  if (changeAmount > 0 && changeAmountNumber >= DUST_LIMIT) {
    feeEstimate = await estimateTransactionFee({
      feePriority,
      numInputs: selectedUTXOs.length,
      numOutputs: 2,
    });
    maxToSpend = selectedTotalValue - feeEstimate;
    changeAmount = BigInt(maxToSpend) - amountInSatoshis;
  }

  const finalChangeAmountNumber = Number(changeAmount);
  const hasChangeOutput =
    changeAmount > 0 && finalChangeAmountNumber >= DUST_LIMIT;

  if (changeAmount > 0 && finalChangeAmountNumber < DUST_LIMIT) {
    feeEstimate += finalChangeAmountNumber;
  }

  return {
    changeAmountNumber: finalChangeAmountNumber,
    feeEstimate,
    hasChangeOutput,
  };
};
